import React from 'react';

import { Flex } from '@lightspeed/flame/Core';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { IconPayments } from '@lightspeed/flame/Icon/Payments';
import { Text } from '@lightspeed/flame/Text';

import format from 'helpers/format';
import { createReactRenderer } from 'helpers/react-renderer';

import { StyledButton, StyledCard, StyledImg } from './PaymentLinkCard.style';

interface PaymentLinkProps {
  remoteDepositAmount: string;
  remoteDepositReceiptSentEmail: string | null;
  remoteDepositUrl: string;
}

const SalePaymentLinkUrl: React.FC<PaymentLinkProps> = ({
  remoteDepositAmount,
  remoteDepositReceiptSentEmail,
  remoteDepositUrl,
}) => {
  const [linkCopied, setLinkCopied] = React.useState(false);

  const { currency_code: currencyCode, lang: locale } = window.merchantos.locale;

  const handleCopy = () => {
    navigator.clipboard.writeText(remoteDepositUrl).then(() => {
      setLinkCopied(true);
    });
  };

  const formatCurrency = () => {
    return (
      <span className="notranslate" data-test="sale-paymentLink-amount">{`${format.toMoney(
        remoteDepositAmount,
        currencyCode,
        locale
      )}`}</span>
    );
  };

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (linkCopied) {
      timeoutId = setTimeout(() => {
        setLinkCopied(false);
      }, 5000);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [linkCopied]);

  return (
    <StyledCard>
      <StyledImg
        data-test="sale-paymentLink-image"
        src={
          remoteDepositReceiptSentEmail
            ? 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/RocketIconEmail.svg'
            : 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/RocketIconLinkOnly.svg'
        }
        alt="image for payment link"
      />
      <Flex flexDirection="column" pl={2}>
        <Text fontSize="19px" fontWeight="bold" pb={2} data-test="sale-paymentLink-title">
          {remoteDepositReceiptSentEmail ? 'Payment link sent!' : 'Payment link generated!'}
        </Text>
        {remoteDepositReceiptSentEmail ? (
          <Text fontSize="14px" data-test="sale-paymentLink-email">
            A <var className="notranslate">{formatCurrency()}</var> payment link was added to your
            receipt and sent by email. You can also copy and share the payment link.
          </Text>
        ) : (
          <Text fontSize="14px" data-test="sale-paymentLink-link-only">
            A <var className="notranslate">{formatCurrency()}</var> payment link was added to your
            receipt. Print or send the receipt by email, or copy and share the payment link where
            you like.
          </Text>
        )}
        <StyledButton onClick={handleCopy} data-test="sale-paymentLink-button">
          {linkCopied ? <IconCheckmark size="18px" /> : <IconPayments size="18px" />}
          <Text data-test="sale-paymentLink-button-text">
            {linkCopied ? 'Payment link copied' : 'Copy payment link'}
          </Text>
        </StyledButton>
      </Flex>
    </StyledCard>
  );
};

export default SalePaymentLinkUrl;

const RenderedComponent = createReactRenderer({
  component: SalePaymentLinkUrl,
  selector: '[data-react-root="SalePaymentLinkUrl"]',
});

history.onChangeState(
  [{ pathname: '/register.php' }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
